import { inject, InjectionToken, signal, Type } from '@angular/core';
import {
  FieldOkParameters,
  FieldOkService,
  FieldOkServiceOptions,
} from '@softline/dynamic';
import {
  BehaviorSubject,
  catchError,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ConnectionHttpService, Sort, SortService } from '@softline/core';
import { SearchResultComponent } from '../abstraction/search-result.component';
import { FavoriteService } from '../../../user/favorites/favorite.service';
import { LastUsedService } from '../../../user/last-used/last-used.service';
import { ResolvableModal } from '@softline/ui-core';
import { map } from 'rxjs/operators';
import { DefaultSearchResultViewComponent, SearchStrategy } from '../search.strategy';

export interface FieldOkSearchStrategyConfig<T extends object> {
  queryFormatter?: (query: string | null) => FieldOkParameters<T>;
  sort?: Sort<T>;
}

export interface FieldOkSearchConfig<T extends object, TModal extends object = object> {
  name: string;
  view: Type<SearchResultComponent<any>>;
  queryModalView?: Type<ResolvableModal<TModal>>;
  priority?: number;
  options?: {
    autoLoad?: boolean;
    serviceOptions?: FieldOkServiceOptions<T>;
  };
}

export const SOFTLINE_CONFIG_FIELD_OK_SEARCH_PAGE = new InjectionToken<
  FieldOkSearchConfig<any>[]
>('FIELD_OK_SEARCH_VIEW');

export class FieldOkSearchStrategy<T extends object> extends SearchStrategy<T> {
  private favoriteService = inject(FavoriteService);
  private lastUsedService = inject(LastUsedService);

  override get queryModalViewType() {
    return this.fokSearchConfig?.queryModalView ?? undefined;
  }

  override activeFilter = signal<{ query: object; fullValue?: object } | null>(null);
  readonly #activeFilter$ = toObservable(this.activeFilter).pipe(
    map((o) => o?.query ?? null)
  );

  protected readonly searchStream$ = new Subject<string | null>();

  protected fokService: FieldOkService<T>;
  protected fokSearchConfig?: FieldOkSearchConfig<any>;
  isManualSearchActive = signal(false);

  private fokConfig$ = new BehaviorSubject<FieldOkSearchConfig<any, any> | null>(null);
  private fokService$ = new BehaviorSubject<FieldOkService<any, any> | null>(null);

  sortService = inject(SortService);
  override get resultViewType() {
    return this.fokSearchConfig?.view ?? DefaultSearchResultViewComponent;
  }

  override results = toSignal(
    this.searchStream$.pipe(
      startWith(null),
      distinctUntilChanged(),
      combineLatestWith(this.#activeFilter$, this.fokConfig$, this.fokService$),
      filter(([o, _, fokConfig, fokService]) => {
        const filter =
          fokConfig?.options?.autoLoad === true ? true : !!o && o?.length > 2;
        return filter && !!fokConfig && !!fokService;
      }),
      switchMap(([filter, filterParams, _, fokService]) => {
        if (!fokService || this.loadingState() === 'loading') return of([]);

        return this.query(fokService, filter, filterParams ?? {}).pipe(
          tap({
            subscribe: () => this.loadingState.set('loading'),
            unsubscribe: () => this.loadingState.set('idle'),
            complete: () => this.loadingState.set('loaded'),
            error: () => this.loadingState.set('error'),
          }),
          catchError(() => of([])),
          map((results) => {
            if (this.config?.sort) {
              return this.sortService.sort(results, this.config?.sort as any);
            } else {
              return results;
            }
          })
        );
      })
    ),
    { initialValue: [] }
  );

  override favorites = this.favoriteService.favorites<T>(this.fieldOkName);
  override lastUsed = this.lastUsedService.lastUsed<T>(this.fieldOkName);

  override loadingState = signal<'loaded' | 'loading' | 'idle' | 'error'>('idle');

  constructor(
    protected fieldOkName: string,
    protected config?: FieldOkSearchStrategyConfig<T>
  ) {
    const connectionHttpService = inject(ConnectionHttpService);
    const searchConfigs = inject(SOFTLINE_CONFIG_FIELD_OK_SEARCH_PAGE);

    super();

    const configs = searchConfigs.filter((o) => o.name === this.fieldOkName);

    configs.sort((a, b) => ((b?.priority || 0) > (a?.priority || 0) ? 1 : -1));

    if (configs.length > 0) {
      this.fokSearchConfig = configs[0];
    }

    if (!this.fokSearchConfig) {
      console.warn(
        `[FieldOkSearchStrategy] - No configuration with name "${fieldOkName}" for token FIELD_OK_SEARCH_VIEW!`
      );
    }

    this.fokService = new FieldOkService<T>(
      connectionHttpService,
      fieldOkName,
      this.fokSearchConfig?.options?.serviceOptions
    );

    this.fokService$.next(this.fokService);
    if (this.fokSearchConfig) this.fokConfig$.next(this.fokSearchConfig);
  }

  searchInputChange(value: string | null) {
    // Normalize input: treat empty string as null
    const normalizedValue = !value || value.trim() === '' ? null : value;

    if (
      (!normalizedValue || normalizedValue?.length <= 2) &&
      this.loadingState() !== 'idle' &&
      this.fokSearchConfig?.options?.autoLoad !== true
    ) {
      this.loadingState.set('idle');
    }

    this.searchStream$.next(normalizedValue);
  }

  async search(value: string | null): Promise<T[]> {
    const service = this.fokService$.value;
    if (!service) return [];

    return await firstValueFrom(this.query(service, value));
  }

  async favoriteChange(value: T): Promise<void> {
    const favorites = this.favorites();
    if (favorites.find((o) => o.id === value['id']))
      await this.favoriteService.remove(this.fieldOkName, value['id']);
    else {
      await this.favoriteService.add(this.fieldOkName, value['id'], value);
    }
  }

  async addToLastUsed(value: T): Promise<void> {
    await this.lastUsedService.add(this.fieldOkName, value['id'], value);
  }

  override setFilter(value: any) {
    this.activeFilter.set(typeof value === 'object' ? { ...value } : null);
  }

  async modalResolved(result: { query: object; fullValue?: object }): Promise<void> {
    this.activeFilter.set(result);
  }

  private query(
    fokService: FieldOkService<any, any>,
    filter: string | null,
    parameters = {}
  ): Observable<T[]> {
    // console.log('query: ', this.fokService, filter);
    return fokService.query(
      this.config?.queryFormatter
        ? this.config.queryFormatter(filter)
        : {
            filter: filter ?? '',
            multiValued: false,
            maxAbfrageResults: 1000,
            parameters,
          }
    ) as Observable<T[]>;
  }
}

export function provideFieldOkSearchStrategy<T extends object = object>(params: {
  name: string;
  config?: FieldOkSearchStrategyConfig<T>;
}) {
  return {
    provide: SearchStrategy,
    useFactory: () => new FieldOkSearchStrategy(params.name, params?.config),
  };
}
